import { type ReactElement, useCallback, useEffect, useState } from 'react';
import { type MerchantFinanceMerchantView, type MerchantPayoutView, type MerchantStatementView } from '@vello/api-client';
import { isIso4217Currency } from '@vello/core';
import { formatMoney } from '@vello/format';
import { AUTO_REFRESH_MS, useAutoRefresh, Badge, Button, Drawer, EmptyState, Field, ListTable, Notice, PageHeader, Panel, Select, TextInput, type Column, FormRow, DrawerHeader, DrawerBody, DrawerFooter, SectionTitle, Cluster, Stack, Text, Divider, HairlineRow, NoteText } from '@vello/web-theme';
import {
  formatMarketInstant,
  markPaidBlock,
  payoutStateTone,
  scopeTimeZone,
  statementOwed,
  tailOf,
} from '../../src/index';
import { useAdmin } from '../session';
import { useConsole } from '../console';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { DrawerStack } from '../components/drawer-stack';
import { IdRef } from '../components/code-ref';
import { usePersonNames } from '../components/person-name';

/**
 * Finance › Repasses a lojistas (W6-ADMIN-SCREENS, D401) — the console over `/v1/merchant-finance/*`, a settlement
 * surface that had no screen at all: statements were generated by a job and paid by hand.
 *
 * **What the ledger owes, never a typed number.** The row shows the statement's net payable minus every payout
 * already initiated or paid against it (a failed payout returns its amount). W6-EARNINGS A2 caps a payout at what
 * the ledger owes, so an operator typing anything else would only ever produce a refusal — this screen never offers
 * an amount field. It initiates no payout at all (review W6N-15: this comment used to claim it did); it settles one.
 * A statement whose payouts could not be read owes an UNKNOWN amount, never its full net payable (W6N-05).
 *
 * Offered on `settlement:view` alone (D405, owner decision on review W6N-01): the merchant picker reads the
 * settlement-scoped list `GET /v1/merchant-finance/merchants` (id + display name only), never the merchant directory
 * (`merchant:view`), so FINANCE works this screen without the directory word.
 *
 * **Four eyes on settlement (D385).** "Mark as paid" needs an external reference and is refused to the operator who
 * initiated the payout (`SELF_APPROVAL`) and to any operator on a payout with no recorded maker
 * (`UNATTRIBUTED_PAYOUT`). The button is not hidden silently: the drawer says which of those it is.
 */
export function MerchantStatementsScreen(): ReactElement {
  const { runtime, me, grants, scope, markets } = useAdmin();
  const { toast } = useConsole();
  const { t, lang } = useT();
  const { v, fail } = useVocab();
  const timeZone = scopeTimeZone(scope.current, markets);

  const [merchants, setMerchants] = useState<readonly MerchantFinanceMerchantView[] | undefined>(undefined);
  const [merchantId, setMerchantId] = useState<string | undefined>(undefined);
  const [statements, setStatements] = useState<readonly MerchantStatementView[] | undefined>(undefined);
  const [payouts, setPayouts] = useState<readonly MerchantPayoutView[]>([]);
  const [unreadable, setUnreadable] = useState<ReadonlySet<string>>(new Set());
  const [listError, setListError] = useState<string | undefined>(undefined);
  const [open, setOpen] = useState<MerchantStatementView | undefined>(undefined);
  const [externalRef, setExternalRef] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | undefined>(undefined);

  const money = useCallback(
    (m: { readonly amountMinor: string; readonly currency: string } | undefined): string => {
      if (m === undefined) return '—';
      return /^-?\d+$/.test(m.amountMinor) && isIso4217Currency(m.currency)
        ? formatMoney({ amountMinor: BigInt(m.amountMinor), currency: m.currency }, { locale: lang, timeZone: 'UTC', measurement: 'metric' })
        : `${m.amountMinor} ${m.currency}`;
    },
    [lang],
  );

  useEffect(() => {
    void (async () => {
      const res = await runtime.merchantFinance.listMerchants();
      if (res.ok) {
        setMerchants(res.value.merchants);
        if (res.value.merchants.length === 1) setMerchantId(res.value.merchants[0]?.merchantId);
      } else {
        setMerchants([]);
        setListError(res.error.code === 'FORBIDDEN' ? t('w6.refused.forbidden') : t('w6.refused', { code: fail(res.error) }));
      }
    })();
  }, [runtime, t, fail]);

  const load = useCallback(async (): Promise<void> => {
    if (merchantId === undefined) {
      setStatements(undefined);
      setPayouts([]);
      setUnreadable(new Set());
      return;
    }
    const res = await runtime.merchantFinance.listStatements(merchantId);
    if (!res.ok) {
      setStatements([]);
      setPayouts([]);
      setUnreadable(new Set());
      setListError(res.error.code === 'FORBIDDEN' ? t('w6.refused.forbidden') : t('w6.refused', { code: fail(res.error) }));
      return;
    }
    setStatements(res.value.statements);
    setListError(undefined);
    // What is still owed can only be told from the payouts already made against each statement. Review W6N-05
    // (Codex P1): a statement whose payout read FAILED is remembered as unreadable — flattening it to "no payouts"
    // presented its full net payable as still owed.
    const all = await Promise.all(res.value.statements.map(async (s) => ({ statementId: s.statementId, res: await runtime.merchantFinance.listPayouts(s.statementId) })));
    setPayouts(all.flatMap(({ res: p }) => (p.ok ? [...p.value.payouts] : [])));
    setUnreadable(new Set(all.filter(({ res: p }) => !p.ok).map(({ statementId }) => statementId)));
  }, [runtime, merchantId, t, fail]);

  /** The payouts of one statement, or `undefined` when they could not be read (the owed amount is then unknown). */
  const payoutsOf = (statementId: string): readonly MerchantPayoutView[] | undefined =>
    unreadable.has(statementId) ? undefined : payouts.filter((p) => p.statementId === statementId);

  useEffect(() => {
    setStatements(undefined);
    void load();
  }, [load]);
  useAutoRefresh(load, { intervalMs: AUTO_REFRESH_MS.queue, enabled: merchantId !== undefined });

  const markPaid = async (payout: MerchantPayoutView): Promise<void> => {
    const ref = externalRef.trim();
    if (ref === '') return;
    setBusy(true);
    setActionError(undefined);
    const res = await runtime.merchantFinance.markPaid(payout.payoutId, { externalRef: ref });
    setBusy(false);
    if (res.ok) {
      setExternalRef('');
      toast({ message: t('mstmt.markPaid.done'), tone: 'success' });
      await load();
      return;
    }
    setActionError(t('w6.actionRefused', { code: fail(res.error) }));
  };

  const owedText = (statement: MerchantStatementView): ReactElement => {
    const owed = statementOwed(statement, payoutsOf(statement.statementId));
    if (owed === undefined) return <Text role="caption" tone="secondary">{t('mstmt.owed.unknown')}</Text>;
    if (owed.amountMinor === 0n) return <Text role="caption" tone="secondary">{t('mstmt.owed.settled')}</Text>;
    return <span className="tnum">{money({ amountMinor: owed.amountMinor.toString(), currency: owed.currency })}</span>;
  };

  const columns: readonly Column<MerchantStatementView>[] = [
    { key: 'statement', header: t('mstmt.col.statement'), width: 'minmax(110px, 0.9fr)', render: (s) => <IdRef value={s.statementId} /> },
    { key: 'date', header: t('mstmt.col.date'), width: 'minmax(120px, 0.9fr)', render: (s) => <Text role="caption" tone="secondary" mono>{formatMarketInstant(s.generatedAt, timeZone, lang, 'date')}</Text> },
    { key: 'lines', header: t('mstmt.col.lines'), width: 'minmax(80px, 0.5fr)', render: (s) => <span className="tnum">{s.lineCount}</span> },
    { key: 'gross', header: t('mstmt.col.gross'), width: 'minmax(110px, 0.9fr)', render: (s) => <span className="tnum">{money(s.grossTotal)}</span> },
    { key: 'net', header: t('mstmt.col.net'), width: 'minmax(110px, 0.9fr)', render: (s) => <span className="tnum">{money(s.netPayable)}</span> },
    { key: 'owed', header: t('mstmt.col.owed'), width: 'minmax(120px, 1fr)', render: owedText },
  ];

  const openPayouts = open === undefined ? [] : (payoutsOf(open.statementId) ?? []);
  const personName = usePersonNames(openPayouts.flatMap((p) => [p.initiatedBy, p.settledBy]));
  const actorName = (id: string): string => (id === me?.id ? t('mstmt.payout.you') : (personName(id) ?? tailOf(id)));
  const openPayoutsUnreadable = open !== undefined && unreadable.has(open.statementId);
  const openOwed = open === undefined ? undefined : statementOwed(open, payoutsOf(open.statementId));

  return (
    <>
      <PageHeader
        title={t('mstmt.title')}
        subtitle={t('mstmt.subtitle')}
      />

      <Panel>
        <Stack gap={4}>
          <Field label={t('stores.field.merchant')} htmlFor="mstmt-merchant" hint={t('stores.needsMerchant.detail')}>
            <Select id="mstmt-merchant" value={merchantId ?? ''} onChange={(e) => setMerchantId(e.target.value === '' ? undefined : e.target.value)}>
              <option value="">{t('stores.merchant.choose')}</option>
              {(merchants ?? []).map((m) => (
                <option key={m.merchantId} value={m.merchantId} title={m.merchantId}>{m.displayName ?? tailOf(m.merchantId)}</option>
              ))}
            </Select>
          </Field>
        </Stack>
      </Panel>


      {merchantId === undefined ? (
        <Panel>
          <EmptyState title={t((merchants ?? []).length === 0 ? 'stores.noMerchants' : 'stores.needsMerchant.title')} {...((merchants ?? []).length === 0 ? {} : { detail: t('stores.needsMerchant.detail') })} />
        </Panel>
      ) : (
        <ListTable
          columns={columns}
          rows={statements ?? []}
          rowKey={(s) => s.statementId}
          onRowClick={(s) => { setOpen(s); setActionError(undefined); setExternalRef(''); }}
          selectedKey={open?.statementId}
          loading={statements === undefined}
          loadingLabel={t('ui.loading')}
          error={listError}
          onRetry={() => void load()}
          retryLabel={t('ui.retry')}
          empty={<EmptyState title={t('mstmt.empty.title')} detail={t('mstmt.empty.detail')} />}
          minWidth={1000}
        />
      )}

      <Drawer open={open !== undefined} onClose={() => setOpen(undefined)} labelledBy="mstmt-detail-title">
        {open !== undefined && (
          <>
            <DrawerHeader titleId="mstmt-detail-title" title={t('mstmt.detail.title', { id: tailOf(open.statementId) })}>
              <Text as="p" role="caption" tone="secondary">{t('mstmt.col.owed')}: {openOwed === undefined ? t('mstmt.owed.unknown') : money({ amountMinor: openOwed.amountMinor.toString(), currency: openOwed.currency })}</Text>
            </DrawerHeader>
            <DrawerBody>
              <DrawerStack>
                {actionError !== undefined && <Notice tone="danger">{actionError}</Notice>}

                <section>
                  <SectionTitle level={3} size="sub">{t('mstmt.detail.lines')}</SectionTitle>
                  {open.lines.slice(0, 20).map((line) => (
                    <HairlineRow key={line.orderRef}>
                      <IdRef value={line.orderRef} />
                      <span className="tnum">{money(line.net)}</span>
                    </HairlineRow>
                  ))}
                </section>

                <section>
                  <SectionTitle level={3} size="sub">{t('mstmt.detail.payouts')}</SectionTitle>
                  {openPayoutsUnreadable ? (
                    <Notice tone="danger">{t('mstmt.payouts.unreadable')}</Notice>
                  ) : openPayouts.length === 0 ? (
                    <Text as="p" role="caption" tone="secondary">{t('mstmt.payouts.empty')}</Text>
                  ) : (
                    openPayouts.map((p) => {
                      const block = markPaidBlock(p, me?.id, grants);
                      return (
                        <Stack key={p.payoutId} gap={2}>
                          <Cluster gap={3}>
                            <Badge tone={payoutStateTone(p.state)}>{v('finance.merchant_payout_state', p.state)}</Badge>
                            <span className="tnum">{money(p.amount)}</span>
                          </Cluster>
                          {p.initiatedBy !== undefined && p.initiatedBy !== null && (
                            <Text role="caption" tone="secondary">{t('mstmt.payout.initiatedBy', { who: actorName(p.initiatedBy) })}</Text>
                          )}
                          {p.settledBy !== undefined && p.settledBy !== null && (
                            <Text role="caption" tone="secondary">{t('mstmt.payout.settledBy', { who: actorName(p.settledBy) })}</Text>
                          )}
                          {p.externalRef !== undefined && <Text role="caption" tone="secondary">{t('mstmt.payout.externalRef', { ref: p.externalRef })}</Text>}
                          {block !== undefined && <NoteText>{v('finance.merchant_payout_settle_block', block)}</NoteText>}
                          {block === undefined && (
                            <FormRow
                              action={
                                <Button variant="primary" disabled={busy || externalRef.trim() === ''} onClick={() => void markPaid(p)}>{t('mstmt.markPaid.action')}</Button>
                              }
                            >
                              <Field label={t('mstmt.markPaid.field')} htmlFor={`mstmt-ref-${p.payoutId}`} hint={t('mstmt.markPaid.hint')}>
                                <TextInput id={`mstmt-ref-${p.payoutId}`} value={externalRef} onChange={(e) => setExternalRef(e.target.value)} />
                              </Field>
                            </FormRow>
                          )}
                        <Divider />
</Stack>
                      );
                    })
                  )}
                </section>
              </DrawerStack>
            </DrawerBody>
            <DrawerFooter>
              <Button variant="ghost" onClick={() => setOpen(undefined)}>{t('ui.close')}</Button>
            </DrawerFooter>
          </>
        )}
      </Drawer>
    </>
  );
}
